import { Injectable } from "@angular/core";
import { NavigationEnd, NavigationStart, Router } from "@angular/router";
import { ShepherdService } from "angular-shepherd";
import { StorageService, StorageServiceKey } from "./storage.service";
import { BehaviorSubject, combineLatest, Observable } from "rxjs";
import { SubscriptionGroup } from "../util/subscriptionGroup";
import { routePermissions } from "../routing-security";
import { PermissionsGuard } from "../guard/permissions.guard";
import { SessionApi } from "../api/session.api";
import Step from "node_modules/angular-shepherd/node_modules/shepherd.js/src/types/step";

@Injectable({
	providedIn: "root",
})
export class TourService {
	private guideSubject = new BehaviorSubject<string | undefined>(undefined);
	private readonly subscriptionGroup = new SubscriptionGroup();
	manageOrdersAccess: boolean;
	isAdmin: boolean | undefined;
	actualUserId: any;
	blockNextNavigation: boolean = false;

	constructor(
		private shepherdService: ShepherdService,
		public router: Router,
		private localstorageSevice: StorageService,
		private permissionsGuard: PermissionsGuard,
		private session: SessionApi
	) {
		this.monitorAccess();
		this.router.events.subscribe((event) => {
			if (event instanceof NavigationStart) {
				if (
					event.navigationTrigger === "popstate" &&
					!this.blockNextNavigation
				) {
					const isTourActive = this.shepherdService.isActive;

					if (isTourActive) {
						const wantToExit = window.confirm("Do you want to exit the tour?");

						if (wantToExit) {
							this.shepherdService.cancel();
						} else {
							this.blockNextNavigation = true;

							this.router
								.navigateByUrl(this.router.url, { replaceUrl: true })
								.then(() => {
									this.blockNextNavigation = false;
								});
						}
					}
				}
			}
		});
	}

	getData() {
		const userData = this.session.$userData.getValue();
		this.isAdmin = userData?.isAdmin;
		this.actualUserId =
			userData?.customerUsers?.length && userData?.customerUsers[0].id;
	}

	monitorAccess() {
		this.getData();
		this.subscriptionGroup.add(
			combineLatest([this.hasRouteAccess("/manage/orders")]).subscribe(
				([manageOrdersAccess]) => {
					this.manageOrdersAccess = this.isAdmin || manageOrdersAccess;
				}
			)
		);
	}

	private get isMobile(): boolean {
		return window.innerWidth <= 768;
	}

	private hasRouteAccess = (k: keyof typeof routePermissions) => {
		return this.permissionsGuard.shouldActivate(routePermissions[k]);
	};

	private buildDesktopSteps() {
		const steps: Array<Step.StepOptions> = [
			{
				id: "dashboard",
				attachTo: { element: "#dashboard", on: "right-end" as const },
				text: `
						<h3>Your command center</h3>
						<p>See open orders, approvals waiting on you, and any alerts. Use the shortcuts to place an order, track deliveries, or view reports</p>
					 `,
				canClickTarget: false,
				beforeShowPromise: () => {
					return new Promise((resolve) => {
						this.router.navigate(["/manage/dashboard"]).then(() => {
							setTimeout(() => resolve(null), 500);
						});
					});
				},
			},
			!this.isAdmin && {
				id: "place-order",
				attachTo: { element: "#place-order", on: "bottom" as const },
				text: `
						<h3>Order approved gear fast</h3>
						<p>Browse your organisation’s approved catalogue. View your balance. Add items to cart, then checkout.</p>
					 `,
			},
			!this.isAdmin && {
				id: "profile-security",
				attachTo: { element: "#manage-accounts", on: "right" as const },
				canClickTarget: false,
				text: `
						<h3>Keep your account secure</h3>
						<p>Update details, set notification preferences, and turn on 2-step verification. SSO is available for eligible clients.
						<a href="#" id="view-account-link" class="text-navy-blue text-decoration-underline">Review security</a>
						</p>
					 `,
				when: {
					show: () => {
						const link = document.getElementById("view-account-link");
						if (link) {
							link.onclick = (e) => {
								e.preventDefault();
								this.getData();
								this.router.navigate([
									"/account/employe/view",
									this.actualUserId,
								]);
								this.shepherdService.next();
							};
						}
					},
				},
				beforeShowPromise: () => {
					return new Promise((resolve) => {
						setTimeout(() => resolve(null), 500);
					});
				},
			},
			this.manageOrdersAccess && {
				id: "manage-orders",
				attachTo: { element: "#manage-orders", on: "left" as const },
				canClickTarget: false,
				text: `
						<h3>Track every order end to end</h3>
						<p>See live status, ETA, and tracking. Split shipments and partial deliveries are listed clearly so nothing gets lost. 
						<a href="#" id="view-order-history-link" class="text-navy-blue text-decoration-underline">View order history</a>
						</p>
					 `,
				when: {
					show: () => {
						const link = document.getElementById("view-order-history-link");
						if (link) {
							link.onclick = (e) => {
								e.preventDefault();
								this.getData();
								this.isAdmin
									? this.router.navigate(["/manage/orders"])
									: this.router.navigate(["/account/orders"]);
								this.shepherdService.next();
							};
						}
					},
				},
				beforeShowPromise: () => {
					return new Promise((resolve) => {
						setTimeout(() => resolve(null), 500);
					});
				},
			},
			{
				id: "collections",
				attachTo: { element: "#manage-collections", on: "right" as const },
				text: `
						<h3>The right products for the right roles</h3>
						<p>Your catalogue is curated for safety and compliance.
						<a href="#" class="text-navy-blue text-decoration-underline" id="view-collection-history-link">Explore Collections</a>
						</p>
					 `,
				canClickTarget: false,
				when: {
					show: () => {
						const link = document.getElementById(
							"view-collection-history-link"
						);
						if (link) {
							link.onclick = (e) => {
								e.preventDefault();
								this.getData();
								this.isAdmin
									? this.router.navigate(["/manage/collections"])
									: this.router.navigate(["/account/collections"]);
								this.shepherdService.next();
							};
						}
					},
				},
				beforeShowPromise: () => {
					return new Promise((resolve) => {
						setTimeout(() => resolve(null), 500);
					});
				},
			},
			{
				id: "sizing-fit",
				attachTo: { element: "#sizing-fit", on: "bottom" as const },
				canClickTarget: false,
				text: `
						<h3>Make the right fit, first time</h3>
						<p>Use the size guide on each product. Saved sizes are remembered for next time.</p>
					 `,
				beforeShowPromise: () => {
					return new Promise((resolve) => {
						setTimeout(() => resolve(null), 500);
					});
				},
			},
			{
				id: "help-support",
				attachTo: { element: ".custom-chat-btn", on: "bottom-end" as const },
				canClickTarget: false,
				text: `
						<h3>We’re here to help</h3>
						<p>Browse the help centre, chat with support, or lodge a ticket. Your account manager’s details are listed here.
						<a href="#" id="open-help-centre" class="text-decoration-underline text-navy-blue">Open help centre</a>
						</p>
					 `,
				when: {
					show: () => {
						const link = document.getElementById("open-help-centre");
						if (link) {
							link.onclick = (e) => {
								e.preventDefault();
								if (window && (window as any).fcWidget) {
									(window as any).fcWidget.open();
								}
								this.setGuide(undefined);
								this.shepherdService.complete();
							};
						}
					},
				},
				beforeShowPromise: () => {
					return new Promise((resolve) => {
						setTimeout(() => resolve(null), 500);
					});
				},
			},
		].filter(Boolean) as Array<Step.StepOptions>;

		const stepsWithExistingElements = steps.filter((step) => {
			const el = step.attachTo?.element;
			if (!el) return true;
			if (typeof el === "string") return !!document.querySelector(el);
			if (typeof el === "function") return !!el();
			return true;
		});

		return stepsWithExistingElements.map((step, index) => {
			const isFirst = index === 0;
			const isLast = index === stepsWithExistingElements.length - 1;

			step.buttons = [
				...(isFirst
					? [
							{
								classes: "btn btn-light",
								text: "Skip",
								action: () => {
									this.setGuide(undefined);
									this.shepherdService.cancel();
								},
							},
					  ]
					: [
							{
								classes: "btn btn-secondary",
								text: "Back",
								action: () => this.shepherdService.back(),
							},
							{
								classes: "btn btn-light",
								text: "Skip",
								action: () => {
									this.setGuide(undefined);
									this.shepherdService.cancel();
								},
							},
					  ]),
				...(isLast
					? [
							{
								classes: "btn btn-success",
								text: "Done",
								action: () => {
									this.setGuide(undefined);
									this.shepherdService.complete();
								},
							},
					  ]
					: [
							{
								classes: "btn btn-primary",
								text: "Next",
								action: () => this.shepherdService.next(),
							},
					  ]),
			];

			return step;
		});
	}

	public startTour() {
		this.shepherdService.defaultStepOptions = {
			cancelIcon: { enabled: true },
			scrollTo: true,
			classes: "shepherd-theme-arrows",
		};

		this.shepherdService.modal = true;
		this.shepherdService.keyboardNavigation = false;

		this.getData();
		const steps = this.buildDesktopSteps();
		this.shepherdService.addSteps(steps);

		(this.shepherdService.tourObject as any).options.exitOnEsc = false;

		if (!this.isMobile) {
			this.shepherdService.start();
		}
	}

	setGuide(sort: string | undefined): void {
		this.guideSubject.next(sort);
	}

	getGuide(): Observable<string | undefined> {
		return this.guideSubject.asObservable();
	}
}
